// Single source of truth for the tenant-console sidebar: grouped by area.
// `labelKey` resolves through the i18n catalog; `label` is the English
// fallback. `icon` is a key looked up in app/shell/nav-icons.tsx.

export interface NavItem {
  label: string;
  labelKey: string;
  path: string;
  /** End-match: only highlight on the exact path. */
  end?: boolean;
  icon: string;
  /** Shown only when this feature is available to the account (asked of the service, never assumed). */
  feature?: 'voice';
  /**
   * For the organisation's owner and admins only (roles, 2 October 2026). An
   * agent does not see it, and opening its address shows a notice instead of
   * the page (app/components/RoleGate.tsx). The services refuse agents too.
   */
  adminOnly?: boolean;
}

export interface NavSection {
  label: string;
  labelKey: string;
  items: NavItem[];
}

/** Address prefixes an agent may not open: every adminOnly item, plus pages under them reached from elsewhere. */
export function adminOnlyPrefixes(): string[] {
  return NAV.flatMap((s) => s.items).filter((i) => i.adminOnly).map((i) => i.path);
}

export const NAV: NavSection[] = [
  {
    label: 'Workspace',
    labelKey: 'nav.section.workspace',
    items: [
      { label: 'Dashboard', labelKey: 'nav.dashboard', path: '/', end: true, icon: 'grid' },
      // Chat insights (29 October 2026): reports quote customers and spend credits, so owners and admins only.
      { label: 'Insights', labelKey: 'nav.insights', path: '/insights', icon: 'insights', adminOnly: true },
      { label: 'Inbox', labelKey: 'nav.inbox', path: '/inbox', icon: 'inbox' }
    ]
  },
  {
    label: 'Audience',
    labelKey: 'nav.section.audience',
    items: [
      { label: 'Contacts', labelKey: 'nav.contacts', path: '/contacts', icon: 'users' },
      // Compose sits between the two on purpose: Contacts is who you know,
      // Compose is messaging a few of them by name, Broadcasts is messaging a
      // group by tag. Someone thinking "message my contacts" looks here, not
      // in the Inbox, which is where it was first put.
      { label: 'Compose', labelKey: 'nav.compose', path: '/inbox/compose', icon: 'compose' },
      { label: 'Broadcasts', labelKey: 'nav.broadcasts', path: '/broadcasts', icon: 'send', adminOnly: true }
    ]
  },
  {
    // Bookings (7 October 2026). Its own section, after the people it is for: the diary is a daily job like the
    // inbox, and setting it up (services, people, classes, settings) sits under it. Agents see every page; the
    // set-up pages are read-only for them and the service refuses their changes.
    label: 'Bookings',
    labelKey: 'nav.section.bookings',
    items: [
      { label: 'Calendar', labelKey: 'nav.bookings.calendar', path: '/bookings', icon: 'calendar' },
      { label: 'Bookings', labelKey: 'nav.bookings.list', path: '/bookings/list', icon: 'list' },
      { label: 'Services', labelKey: 'nav.bookings.services', path: '/bookings/services', icon: 'tag' },
      { label: 'Staff and hours', labelKey: 'nav.bookings.staff', path: '/bookings/staff', icon: 'clock' },
      { label: 'Classes', labelKey: 'nav.bookings.classes', path: '/bookings/classes', icon: 'users' },
      { label: 'Booking settings', labelKey: 'nav.bookings.settings', path: '/bookings/settings', icon: 'sliders' }
    ]
  },
  {
    // Tickets (7 October 2026). After Bookings: queries from every channel become tickets the team works
    // through. Agents see every page; the settings are read-only for them and the service refuses their changes.
    label: 'Tickets',
    labelKey: 'nav.section.tickets',
    items: [
      { label: 'Queue', labelKey: 'nav.tickets.queue', path: '/tickets', icon: 'ticket' },
      { label: 'Board', labelKey: 'nav.tickets.board', path: '/tickets/board', icon: 'board' },
      { label: 'My tasks', labelKey: 'nav.tickets.tasks', path: '/tickets/tasks', icon: 'tasks' },
      { label: 'Reports', labelKey: 'nav.tickets.reports', path: '/tickets/reports', icon: 'chart' },
      { label: 'Ticket settings', labelKey: 'nav.tickets.settings', path: '/tickets/settings', icon: 'sliders' }
    ]
  },
  {
    label: 'Automation',
    labelKey: 'nav.section.automation',
    items: [
      { label: 'Templates', labelKey: 'nav.templates', path: '/templates', icon: 'file', adminOnly: true },
      { label: 'Flows', labelKey: 'nav.flows', path: '/flows', icon: 'flow', adminOnly: true },
      { label: 'Knowledge base', labelKey: 'nav.knowledge', path: '/knowledge', icon: 'book', adminOnly: true },
      // AI tools (14 October 2026): spends credits, so owners and admins only.
      { label: 'AI tools', labelKey: 'nav.aiTools', path: '/ai', icon: 'spark', adminOnly: true }
    ]
  },
  {
    label: 'Account',
    labelKey: 'nav.section.account',
    items: [
      { label: 'Numbers', labelKey: 'nav.numbers', path: '/numbers', icon: 'phone', adminOnly: true },
      // Facebook Messenger and Instagram. Beside Numbers rather than under
      // Settings, because connecting a place customers write to is the same
      // kind of job as attaching a number, not a preference. The page names
      // WhatsApp and the website widget too and links to where each is set up,
      // so "add a channel" has one answer wherever somebody starts looking.
      { label: 'Channels', labelKey: 'nav.channels', path: '/channels', icon: 'inbox', adminOnly: true },
      // Phone calls answered by the assistant. Hidden unless the platform has
      // voice switched on: see lib/voice.ts.
      { label: 'Voice', labelKey: 'nav.voice', path: '/voice', icon: 'call', adminOnly: true, feature: 'voice' },
      { label: 'Billing', labelKey: 'nav.billing', path: '/billing', icon: 'wallet', adminOnly: true },
      // W47. Under Account because it is about this account's own money, not
      // about messaging: a partner opens it to see what they have earned and
      // to ask for it.
      { label: 'Refer and earn', labelKey: 'nav.refer', path: '/refer', icon: 'gift', adminOnly: true },
      // Top level rather than a page under Settings: it is what somebody wiring
      // their own system to this one opens and keeps open, not something they
      // visit once while configuring an account. It sits beside Settings, where
      // the API keys it documents are made.
      { label: 'Developers', labelKey: 'nav.developers', path: '/developers', icon: 'code', adminOnly: true },
      { label: 'Settings', labelKey: 'nav.settings', path: '/settings', icon: 'cog' }
    ]
  }
];

/**
 * The one menu item the page belongs to (4 October 2026): the longest item path the address is at or under.
 * Each item used to test itself alone with `startsWith`, so on /inbox/compose both Inbox and Compose matched and
 * Inbox lit up. A path matches only at a segment boundary (/refer does not match /referrals); `/` matches only
 * itself.
 */
export function activeNavPath(pathname: string, paths: readonly string[]): string | null {
  let best: string | null = null;
  for (const p of paths) {
    const hit = p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(p.endsWith('/') ? p : p + '/');
    if (hit && (best === null || p.length > best.length)) best = p;
  }
  return best;
}
